import { c as _c } from "react/compiler-runtime";
import { Skeleton, Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { PreviewErrorBoundary } from "@pages/project-details/project-markups/sidepanel/preview/preview-error-boundary";
import { AnnotationSidePanelEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { lazy, Suspense, useRef } from "react";
// Keep the lotv renderers out of the main bundle until a preview is shown
const MarkupPanoramaCanvas = lazy(async () => ({
  default: (await import("@pages/project-details/project-markups/sidepanel/preview/panorama/markup-panorama-canvas")).MarkupPanoramaCanvas
}));
const MarkupPointCloudCanvas = lazy(async () => ({
  default: (await import("@pages/project-details/project-markups/sidepanel/preview/point-cloud/markup-point-cloud-canvas")).MarkupPointCloudCanvas
}));
function PreviewCanvas(t0) {
  const $ = _c(5);
  const {
    content,
    onCameraChange
  } = t0;
  let t1;
  if ($[0] !== content.kind || $[1] !== content.panorama || $[2] !== content.pointCloud || $[3] !== onCameraChange) {
    t1 = content.kind === "panorama" ? <MarkupPanoramaCanvas panorama={content.panorama} onCameraChange={onCameraChange} /> : <MarkupPointCloudCanvas pointCloud={content.pointCloud} onCameraChange={onCameraChange} />;
    $[0] = content.kind;
    $[1] = content.panorama;
    $[2] = content.pointCloud;
    $[3] = onCameraChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Previews the annotation in the panorama it was placed on, or else the point cloud around it,
 * rendered in the Dashboard. Nothing is shown when there is neither, or if rendering fails.
 * Finding out whether there is one is part of loading the side panel, so this only waits for
 * the preview itself to load.
 */
export function MarkupPreview(t0) {
  const $ = _c(23);
  const {
    markupId,
    previewState,
    onCameraChange
  } = t0;
  const {
    trackEvent
  } = useTrackEvent();
  const trackedMarkupIdRef = useRef(undefined);
  let t1;
  if ($[0] !== markupId || $[1] !== previewState.content || $[2] !== previewState.status || $[3] !== trackEvent) {
    t1 = function trackNavigation(interaction) {
      if (previewState.status !== "ready" || trackedMarkupIdRef.current === markupId) {
        return;
      }
      trackedMarkupIdRef.current = markupId;
      trackEvent({
        name: AnnotationSidePanelEvents.navigatePreview,
        props: {
          kind: previewState.content.kind,
          interaction
        }
      });
    };
    $[0] = markupId;
    $[1] = previewState.content;
    $[2] = previewState.status;
    $[3] = trackEvent;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const trackNavigation = t1;
  if (previewState.status === "unavailable" || previewState.status === "finding") {
    return null;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton variant="rectangular" sx={{
      width: "100%",
      height: "100%"
    }} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const skeleton = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      mt: 5
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text variant="heading12" sx={{
      color: "text.secondary",
      mb: 1
    }}>Preview</Text>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== trackNavigation) {
    t5 = () => trackNavigation("drag");
    t6 = () => trackNavigation("zoom");
    $[8] = trackNavigation;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      position: "relative",
      aspectRatio: "16 / 9",
      borderRadius: 1,
      overflow: "hidden",
      backgroundColor: "action.hover"
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onCameraChange || $[13] !== previewState.content || $[14] !== previewState.status) {
    t8 = previewState.status === "ready" ? <Suspense fallback={skeleton}><PreviewCanvas content={previewState.content} onCameraChange={onCameraChange} /></Suspense> : skeleton;
    $[12] = onCameraChange;
    $[13] = previewState.content;
    $[14] = previewState.status;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t8) {
    t9 = <Box sx={t3} data-testid="markup-side-panel-preview">{t4}<Box onPointerDown={t5} onWheel={t6} sx={t7}>{t8}</Box></Box>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== markupId || $[21] !== t9) {
    t10 = <PreviewErrorBoundary key={markupId} fallback={null}>{t9}</PreviewErrorBoundary>;
    $[20] = markupId;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}